import React from "react"
import Typography from "aldoo-ra/Typography"
import VariantReader from "aldoo-ra/VariantReader"
import { twMerge } from "tailwind-merge"

// Grid cell type renderers
const cellRenderers = {
  // Large stat with label
  stat: ({ label, value, suffix, valueColor, labelColor }, styles) => (
    <>
      <Typography
        variant="admin_txt1"
        className={labelColor || styles.stat_label}
      >
        {label}
      </Typography>
      <div className={styles.stat_value_wrapper}>
        <Typography
          variant="admin_h2"
          className={twMerge(styles.stat_value, valueColor)}
        >
          {value}
        </Typography>
        {suffix && (
          <Typography
            variant="admin_h4"
            className={twMerge(
              styles.stat_suffix,
              labelColor || styles.stat_label
            )}
          >
            {suffix}
          </Typography>
        )}
      </div>
    </>
  ),

  // Medium stat with label
  statMedium: ({ label, value, suffix, valueColor, labelColor }, styles) => (
    <>
      <Typography
        variant="admin_txt2"
        className={twMerge(styles.stat_medium_label, labelColor)}
      >
        {label}
      </Typography>
      <div className={styles.stat_medium_value_wrapper}>
        <Typography
          variant="admin_h3"
          className={twMerge(styles.stat_medium_value, valueColor)}
        >
          {value}
        </Typography>
        {suffix && (
          <Typography
            variant="admin_body"
            className={twMerge(styles.stat_medium_suffix, labelColor)}
          >
            {suffix}
          </Typography>
        )}
      </div>
    </>
  ),

  // Simple text display
  text: ({ label, value, valueColor, labelColor }, styles) => (
    <div className="relative h-full flex flex-col justify-between">
      <Typography
        variant="admin_txt2"
        className={twMerge(styles.text_label, labelColor, "mt-0")}
      >
        {label}
      </Typography>

      <Typography
        variant="admin_body"
        className={twMerge(styles.text_value, valueColor, "mb-0")}
      >
        {value}
      </Typography>
    </div>
  ),

  // Monospace text (for codes, IDs)
  code: ({ label, value, labelColor, action }, styles) => (
    <div className="relative h-full flex flex-col justify-between">
      <Typography
        variant="admin_txt2"
        className={twMerge(styles.code_label, labelColor)}
      >
        {label}
      </Typography>

      <Typography 
        variant="admin_body" 
        className={twMerge(styles.code_value, "font-bold my-0")}
      >
        {value}
      </Typography>

      {action && (
        <button
          onClick={action.onClick}
          className="absolute right-0 top-1/2 transform -translate-y-1/2 p-1 text-text_secondary hover:text-primary transition-colors cursor-pointer"
          title={action.tooltip || "Action"}
        >
          <div className="w-5 h-5">
            {action.icon}
          </div>
        </button>
      )}
    </div>
  ),

  // Dark header section
  darkHeader: ({ sections }, styles) => (
    <div className={styles.dark_header}>
      <div className={styles.dark_header_grid}>
        {sections.map((section, idx) => (
          <div
            key={idx}
            className={twMerge(
              styles.dark_header_section,
              idx < sections.length - 1 && styles.dark_header_divider
            )}
          >
            {cellRenderers.stat(
              {
                ...section,
                labelColor: section.labelColor || styles.dark_header_label,
                valueColor: section.valueColor || styles.dark_header_value,
              },
              styles
            )}
          </div>
        ))}
      </div>
    </div>
  ),

  // Highlighted/special cell
  highlight: ({ icon, title, subtitle, color = "admin_success" }, styles) => {
    // Default highlight styles if nothing else works
    const defaultHighlightStyles = {
      container:
        "bg-admin_success/10 border border-admin_success/20 rounded-lg p-4",
      content: "flex items-center space-x-3",
      icon_wrapper:
        "w-10 h-10 bg-admin_success/20 rounded-full flex items-center justify-center",
      title: "text-admin_success font-medium",
      subtitle: "text-admin_text_secondary",
    }

    // Try to get custom styles, fall back to defaults
    let highlightStyles = defaultHighlightStyles

    if (styles && typeof styles.getHighlightStyles === "function") {
      const customStyles = styles.getHighlightStyles(color)
      if (customStyles && typeof customStyles === "object") {
        highlightStyles = customStyles
      }
    } else if (styles && styles.highlight_default) {
      highlightStyles = styles.highlight_default
    }

    return (
      <div className={highlightStyles.container}>
        <div className={highlightStyles.content}>
          {icon && <div className={highlightStyles.icon_wrapper}>{icon}</div>}
          <div>
            <Typography variant="admin_body" className={highlightStyles.title}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="admin_txt1"
                className={highlightStyles.subtitle}
              >
                {subtitle}
              </Typography>
            )}
          </div>
        </div>
      </div>
    )
  },

  // Badge/tag style
  badge: ({ label, value, badge }, styles) => (
    <div className={styles.badge_container}>
      <Typography variant="admin_txt2" className={styles.badge_label}>
        {label}
      </Typography>
      <Typography variant="admin_body" className={styles.badge_value}>
        {value}
      </Typography>
      {badge && <div className={styles.badge_tag}>{badge}</div>}
    </div>
  ),

  // Empty spacer
  spacer: () => null,

  // Custom JSX content renderer
  custom: ({ label, value, labelColor }, styles) => {
    let content
    
    // If value is a React element or JSX, use it directly
    if (React.isValidElement(value)) {
      content = value
    } 
    // If value is a function, call it to get the JSX
    else if (typeof value === 'function') {
      content = value()
    }
    // If value is a string or other primitive, wrap it in a div
    else if (typeof value === 'string' || typeof value === 'number') {
      content = <div className={styles.custom_text}>{value}</div>
    }
    // If value is an object with content property, render that
    else if (value && typeof value === 'object' && value.content) {
      content = React.isValidElement(value.content) ? value.content : <div className={styles.custom_text}>{value.content}</div>
    }
    // Default fallback
    else {
      content = <div className={styles.custom_text}>No content</div>
    }

    // Render with label if provided
    return (
      <div className="relative h-full flex flex-col justify-between">
        {label && (
          <Typography
            variant="admin_txt2"
            className={twMerge(styles.custom_label || styles.text_label, labelColor)}
          >
            {label}
          </Typography>
        )}
        <div className="flex-1">
          {content}
        </div>
      </div>
    )
  },
}

export default function GridSummary({
  config,
  variant = "admin",
  styleOverrides = {},
  className,
  containerClassName,
}) {
  const { layout = [] } = config

  // Get styles from variant
  const styles = VariantReader.useVariant({
    componentName: "GridSummary",
    variant,
    styleOverrides,
  })

  return (
    <div className={twMerge(styles.container, containerClassName)}>
      {layout.map((section, sectionIdx) => {
        // Handle special full-width sections
        if (section.type === "darkHeader") {
          return (
            <div key={sectionIdx} className={styles.dark_header_wrapper}>
              {cellRenderers.darkHeader(section, styles)}
              {section.children && (
                <div className={styles.dark_header_children}>
                  {renderGrid(section.children.cells || [])}
                </div>
              )}
            </div>
          )
        }

        // Regular grid sections
        return (
          <div
            key={sectionIdx}
            className={twMerge(styles.section, section.className)}
          >
            {renderGrid(section.cells || [section])}
          </div>
        )
      })}
    </div>
  )

  function renderGrid(cells) {
    // Ensure cells is an array
    if (!Array.isArray(cells)) {
      console.warn("GridSummary: cells must be an array", cells)
      return null
    }

    const gridCols = Math.max(
      ...cells.map((cell) => (cell.colStart || 1) + (cell.colSpan || 1) - 1)
    )

    return (
      <div
        className={twMerge(
          styles.grid,
          styles[`grid_cols_${gridCols}`] || `grid-cols-${gridCols}`
        )}
      >
        {cells.map((cell, cellIdx) => {
          const renderer = cellRenderers[cell.type]
          if (!renderer) {
            console.warn(`Unknown cell type: ${cell.type}`)
            return null
          }

          const colSpan = cell.colSpan || 1
          const cellClassName = twMerge(
            cell.type !== "spacer" && styles.grid_cell,
            styles[`col_span_${colSpan}`] || `col-span-${colSpan}`,
            cell.className
          )

          return (
            <div key={cellIdx} className={cellClassName}>
              {renderer(cell, styles)}
            </div>
          )
        })}
      </div>
    )
  }
}
